import PropTypes from 'prop-types';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Card, CardContent, Divider, Stack, Tooltip, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { toAbsoluteUrl } from '../../../components/seo';
// utils
import { fDate } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

// Share targets chosen for how people actually pass links around in the two
// markets this course is sold into: WhatsApp is the dominant channel in both
// India and the UAE, Facebook and LinkedIn carry the professional audience,
// Telegram is strong in the region, and X covers the rest.
const SHARE_TARGETS = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'eva:whatsapp-fill',
    color: '#25D366',
    href: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'eva:facebook-fill',
    color: '#1877F2',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: 'eva:linkedin-fill',
    color: '#0A66C2',
    href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: 'eva:paper-plane-fill',
    color: '#229ED9',
    href: (url, title) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
  {
    id: 'x',
    label: 'X',
    icon: 'eva:twitter-fill',
    color: '#161C24',
    href: (url, title) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  },
];

const CardRoot = styled(Card)(({ theme }) => ({
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 10px 30px ${alpha(theme.palette.grey[900], 0.06)}`,
}));

// Ties the card to the course hero above it without adding another hard edge.
const CardAccent = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: 3,
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0)})`,
}));

const StatTile = styled('div')(({ theme }) => ({
  flexGrow: 1,
  minWidth: 88,
  padding: theme.spacing(1.5, 1),
  textAlign: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  transition: 'background-color 0.3s ease, transform 0.3s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      transform: 'translateY(-3px)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

// Circular icon button. `pointerEvents` stays on so the anchor remains a real
// link, and the brand colour only paints the glyph so the row stays quiet.
const ShareButton = styled('a')(({ theme }) => ({
  width: 36,
  height: 36,
  display: 'grid',
  placeItems: 'center',
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.25s ease, background-color 0.25s ease, transform 0.25s ease',
  '@media (hover: hover)': {
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: theme.palette.background.neutral,
      borderColor: 'currentColor',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

// ----------------------------------------------------------------------

CourseDetailCard.propTypes = {
  course: PropTypes.object,
  // Optional override so a host page can feed counts from elsewhere.
  stats: PropTypes.array,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  showShare: PropTypes.bool,
  sx: PropTypes.object,
};

/**
 * "At a glance" summary for a course: the headline counts plus the handful of
 * facts a prospective student checks before enrolling, and a share row so a
 * candidate can pass the course on. Every value comes from the course payload —
 * nothing here is invented.
 */
export default function CourseDetailCard({
  course,
  stats: statsProp,
  eyebrow = 'Course overview',
  title = 'What is included',
  showShare = true,
  sx,
}) {
  if (!course) {
    return null;
  }

  const { name, slug, tag, addedAt, subjectCount, videoCount, materialCount, chapterCount } = course;

  const stats =
    statsProp ??
    [
      { id: 'videos', icon: 'ic:round-play-circle', value: videoCount, label: 'Videos' },
      { id: 'materials', icon: 'ic:round-pdf', value: materialCount, label: 'Materials' },
      { id: 'chapters', icon: 'ic:round-account-tree', value: chapterCount, label: 'Chapters' },
      { id: 'subjects', icon: 'ic:round-menu-book', value: subjectCount, label: 'Subjects' },
    ].filter((stat) => Number.isFinite(stat.value));

  const facts = [
    { id: 'tag', label: 'Discipline', value: tag?.name },
    { id: 'published', label: 'Published', value: addedAt ? fDate(addedAt) : null },
    { id: 'ref', label: 'Course code', value: slug },
  ].filter((fact) => Boolean(fact.value));

  if (!stats.length && !facts.length) {
    return null;
  }

  // Share links need an absolute URL; relative paths would resolve against
  // whatever host the visitor landed on. `toAbsoluteUrl` reuses the same origin
  // as the page canonical, so a shared link always matches the indexed one.
  const shareUrl = toAbsoluteUrl(`/course/${slug}`);
  const shareTitle = `${name} | ASAP UAE`;

  // The native sheet is the fastest path on the mobile-first audiences this
  // course targets, so the primary action uses it when the browser has one and
  // falls back to WhatsApp — the dominant channel in both markets — when not.
  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: name, url: shareUrl });
        return;
      } catch (error) {
        // A user-cancelled share rejects; anything else falls through to the
        // WhatsApp link rather than leaving the tap with no effect.
      }
    }

    window.open(SHARE_TARGETS[0].href(shareUrl, shareTitle), '_blank', 'noopener,noreferrer');
  };

  return (
    <MotionInView variants={varFadeInUp} transition={{ duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] }}>
      <CardRoot sx={{ ...sx, height: 'auto' }}>
        <CardAccent />

        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          {eyebrow && (
            <Typography
              variant="subtitle2"
              sx={{
                color: 'primary.main',
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: 1.1,
                fontSize: 11,
              }}
            >
              {eyebrow}
            </Typography>
          )}

          {title && (
            <Typography
              component="h2"
              variant="h6"
              sx={{ color: 'text.primary', mb: stats.length ? 2.5 : 2 }}
            >
              {title}
            </Typography>
          )}

          {stats.length > 0 && (
            <Stack direction="row" spacing={1.25} sx={{ mb: facts.length ? 2.5 : 0, flexWrap: 'wrap' }} useFlexGap>
              {stats.map((stat) => (
                <StatTile key={stat.id}>
                  <Iconify icon={stat.icon} width={22} sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {stat.label}
                  </Typography>
                </StatTile>
              ))}
            </Stack>
          )}

{facts.length > 0 && (
              <Stack spacing={1.25}>
                {facts.map((fact) => (
                  <Stack key={fact.id} direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                      {fact.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'right', minWidth: 0 }}
                    >
                      {fact.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

          {showShare && (
            <>
              <Divider sx={{ mt: 2.5, mb: 2 }} />

              <Typography
                component="h3"
                variant="subtitle2"
                sx={{ color: 'text.primary', mb: 1.25 }}
              >
                Share this course
              </Typography>

              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                <Tooltip title="Share">
                  <ShareButton
                    component="button"
                    type="button"
                    onClick={handleNativeShare}
                    aria-label="Share this course"
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.primary.main}`,
                      backgroundColor: 'primary.main',
                      color: 'common.white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                  >
                    <Iconify icon="eva:share-2-fill" width={18} sx={{ color: 'inherit' }} />
                  </ShareButton>
                </Tooltip>

                {SHARE_TARGETS.map((target) => (
                  <Tooltip key={target.id} title={target.label}>
                    <ShareButton
                      href={target.href(shareUrl, shareTitle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Share on ${target.label}`}
                      sx={{ color: target.color }}
                    >
                      <Iconify icon={target.icon} width={18} sx={{ color: 'inherit' }} />
                    </ShareButton>
                  </Tooltip>
                ))}
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 1.5, lineHeight: 1.6 }}>
                Know someone preparing for this exam? Send them the course link.
              </Typography>
            </>
          )}
        </CardContent>
      </CardRoot>
    </MotionInView>
  );
}
